import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface MachinationHistoryPageProps {
}

const MachinationHistoryPage = (props: MachinationHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_post_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Machination Age - Chase the Fox`,
        description: "The Machination Age of Casarok saw the world engulfed in a war using machine weapons!",
        keywords: ["casarok", "casarok history", "machination age", "fantasy timeline", "fantasy world history", "modern fantasy", "fantasy world war age"],
        imgSrc: _map_src,
        url: `world/history/machination-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Post-Cataclysm Map'
                         alt="A map of Casarok after the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Machination Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_post_cataclysm.png`)}
                             title='Casarok Post-Cataclysm Map'
                             alt="A map of Casarok after the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years 1800 - 1900 ACE.</em>
                    </p>
                    <p>
                        The Machination Age of Casarok is marked by two major events. The first is the invention
                        of <NavLink className="world-link" to="/world/magic/items/mage-tech">mage-tech</NavLink> which
                        are machine (and later computational) parts enchanted with passive or activatable spells. It
                        allowed for rapid technological advancement and finally united the realms of magic and
                        technology that had been divided for so long. However, this also gave way to the second event
                        that happened during this age. The Machinization War took hold throughout the world as the rogue
                        nation Mechanis was formed. They used the new mage-tech technology to build machine weapons, and
                        began to conquer the world. Two groups of countries were formed that opposed each other in this
                        world war.
                    </p>
                    <p>
                        The <em>Mechanistian Federation</em> was formed with Mechanis as the central nation. Joining
                        them in the war was Polonia, Streepan, Tezcatli, Vincito, Elinea, and Phaerolis. Minor nations
                        in the Federation included Khetus, Donbey, Derisak, and Ramos.
                    </p>
                    <p>
                        Opposing them was the <em>Rebel Coalition</em>. The Coalition was primarily led by Arceann,
                        Erthuria, Haluthe, Iwattu, and Vechnya, Couri, Horpeta, Notahbe, and Zendeland also contributed
                        in battles.
                    </p>
                    <p>
                        <em>How does Casarok's Machination Age differ from the world wars of Earth?</em><br/>
                        Magic is the main difference here as Earth has no mage-tech equivalent. Beyond that, the use of
                        simple robots in combat during the Machinization War was prevalent by the Federation. The
                        Rebel Coalition had to catch up to that kind of power for the first half of the war. Airships
                        were converted for use in combat at this time as magical attack vessels. Planes and the later
                        jets used guns and explosives instead as they do on Earth.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>1802</em>: Mage-tech is invented. The first machine to utilize mage-tech had small
                            gears that resisted corrosion and friction to improve efficiency.
                        </li>
                        <li>
                            <em>1813</em>: Minor conflicts erupt around the world leading to a near world war situation.
                        </li>
                        <li>
                            <em>1832</em>: Stock markets across the world plummet, leading to a worldwide economic
                            crisis.
                        </li>
                        <li>
                            <em>1844</em>: The Alle-Mechanisch Party formed in Berun and took the country by storm due
                            to weaknesses perceived in the government there.
                        </li>
                        <li>
                            <em>1847</em>: The nation of Mechanis establishes itself from Berunian land by winning
                            battles through force, ideology, and propaganda.
                        </li>
                        <li>
                            <em>1853</em>: Mechanis invades Czarnia and begins capturing some of its lands. They soon
                            begin conquering parts of Berun, Tilland, Vytauland, and Volad.
                        </li>
                        <li>
                            <em>1854</em>: The Mechanistian Federation is formed when Polonia aligned with Mechanis.
                            Arceann declares war on the Federation.
                        </li>
                        <li>
                            <em>1855</em>: Erthuria and Haluthe join Arceann and form the Rebel Coalition. Tezcatli,
                            Phaerolis, and Vincito join Mechanis.
                        </li>
                        <li>
                            <em>1856</em>: Mechanis invades and begins to conquer Arceann, weakening the Coalition. The
                            Federation is joined by Ramos, Khetus, Elinea, and Donbey. Iwattu joins the Coalition.
                        </li>
                        <li>
                            <em>1857</em>: The Federation and Coalition form into their final membership states. Arceann
                            is mostly conquered. Mechanis plans an invasion of Erthuria and Haluthe. The Marsupians
                            begin to rebel against Phaerolis while their attention is turned on the war.
                        </li>
                        <li>
                            <em>1859</em>: The horrors of Mechanis are fully revealed to the world. Mechanis is
                            stripping mana from those they capture in a plot to eliminate all organic life on Casarok
                            by turning them all into robots.
                        </li>
                        <li>
                            <em>1860</em>: The Coalition frees Arceann and continues to reclaim land from the
                            Mechanistian Federation.
                        </li>
                        <li>
                            <em>1862</em>: Mechanis folds, and its land is taken by the Rebel Coalition. Polonia
                            surrenders soon after.
                        </li>
                        <li>
                            <em>1863</em>: The first atomic bombs are dropped in Tezcatli by the Coalition. Tezcatli
                            becomes the final nation of the Federation to surrender, ending the Mechanization War.
                        </li>
                        <li>
                            <em>1868</em>: The Reverie Economic Zone is founded, using land ceded by Mechanis and
                            Polonia with Coalition nations providing some land in support as well.
                        </li>
                        <li>
                            <em>1869</em>: The nation of Cormandy is formed, seceding from the remnants of Polonia.
                            The free state of Port Khamen is created from Phaerolian land.
                        </li>
                        <li>
                            <em>1871</em>: The Marsupians win in their rebellion against Phaerolis, economically and
                            militarily devastated from years of war and surrender. Marsupia is formed from half of the
                            Phaerolian continent, giving them their first free state in centuries.
                        </li>
                        <li>
                            <em>1872</em>: Ramos falls at the hands of Horpeta and Notahbe. The land is split between
                            those two nations. Some land is returned to Derisak.
                        </li>
                        <li>
                            <em>1874</em>: The Tezcatli Empire collapses. In their place, the countries of Tenoch, Iuta,
                            and Cojobi are formed.
                        </li>
                        <li>
                            <em>1877</em>: The Federation of Sovereign States is established by Berun and various
                            Coalition countries as an organization built to maintain peace in the world.
                        </li>
                        <li>
                            <em>1880</em>: The International Council of Magic is moved from Erthuria to the Reverie
                            Economic Zone.
                        </li>
                        <li>
                            <em>1884</em>: The telephone is invented.
                        </li>
                        <li>
                            <em>1888</em>: The Strepanese Sultanate falls. A new government is established, and the
                            nation of Kapprione is formed.
                        </li>
                        <li>
                            <em>1895</em>: Yamachi splinters off from Hantoi to form their own nation due to concerns of
                            religious persecution and the safety of their people.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default MachinationHistoryPage;